import type { CatalogProperty, PropertyDisplayCode } from 'src/api/generated';
import { isRecord } from 'src/modules/shared/utils/types.util';

function propertyId(value: unknown): number | undefined {
  const id =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d+$/.test(value)
        ? Number(value)
        : NaN;
  return Number.isSafeInteger(id) && id > 0 && id <= 2_147_483_647
    ? id
    : undefined;
}

function searchPropertyIds(outputs: Record<string, unknown>): unknown[] {
  const result = isRecord(outputs.result) ? outputs.result : {};
  const response = isRecord(outputs.responseData) ? outputs.responseData : {};
  const items: unknown[] =
    [outputs.items, result.items, response.items].find(Array.isArray) ?? [];
  return items.flatMap((item) =>
    isRecord(item) ? [item.id ?? item.propertyId] : []
  );
}

function stepPropertyIds(step: unknown): unknown[] {
  if (!isRecord(step) || !isRecord(step.outputs)) return [];
  const outputs = step.outputs;
  const property = isRecord(outputs.propertyOfInterest)
    ? outputs.propertyOfInterest
    : undefined;
  const ids = [property?.id ?? outputs.propertyOfInterestId];
  const tool = typeof step.toolName === 'string' ? step.toolName : '';
  if (tool.includes('set-conversation-property-of-interest')) {
    ids.push(outputs.propertyId);
  }
  if (tool.includes('search-properties')) {
    ids.push(...searchPropertyIds(outputs));
  }
  return ids;
}

/**
 * Collect known property IDs from history without treating arbitrary IDs as properties.
 * @param steps - Unmodified execution steps.
 * @returns Unique property IDs for catalog reads.
 */
export function executionPropertyIds(steps: readonly unknown[]): number[] {
  const ids = steps.flatMap(stepPropertyIds).flatMap((value) => {
    const id = propertyId(value);
    return id === undefined ? [] : [id];
  });
  return [...new Set(ids)];
}

/**
 * Match an execution's stable ID to a separately fetched catalog property.
 * @param properties - Authorized catalog responses from the query cache.
 * @param idValue - Historical internal ID, never an external code.
 * @returns Backend-selected display code, or undefined to retain the historical label.
 */
export function findExecutionPropertyCode(
  properties: ReadonlyMap<number, CatalogProperty> | undefined,
  idValue: unknown
): PropertyDisplayCode | undefined {
  const id = propertyId(idValue);
  return id === undefined ? undefined : properties?.get(id)?.displayCode;
}
